import type {
  AnswerView,
  BoxByCodeView,
  BoxCodeView,
  BoxOwnerView,
  BoxPublicView,
  CreateQuestionResponse,
  LeaderboardResponse,
  PrepareShareResponse,
  QuestionAnswerView,
  QuestionInboxItem,
  RewardsResponse,
} from '@veylme/shared';

import { env } from '../env.js';

/**
 * Mock API для local dev (вне сервера) — Веха 13.x design-loop.
 *
 * Зачем: экраны берут данные через `apiFetch` → реальный `fetch`. С mock-окружением
 * Telegram (`VITE_USE_TG_MOCK`) initData фейковый и сервер его не пропустит, поэтому
 * вживую крутить вёрстку нельзя. Этот модуль перехватывает `window.fetch` и отдаёт
 * заранее подготовленные ответы, валидные по zod-схемам `@veylme/shared` (иначе
 * `apiFetch` → `parse.parse` всё равно бросит).
 *
 * Активируется ТОЛЬКО под `env.VITE_USE_TG_MOCK` (тот же флаг, что и mock Telegram).
 * В production-сборку не попадает (env.ts роняет prod при VITE_USE_TG_MOCK=true).
 *
 * Неизвестные пути проксируются в реальный fetch (с base `http://mock.local` он
 * упадёт — это сигнал, что добавили ручку и забыли мок).
 */

// ISO-8601 строки (z.string().datetime()) — фиксированные, без Date.now (детерминизм).
const FIXTURE_BOX: BoxOwnerView = {
  id: 'box_mock_1',
  token: 'mocktoken1234',
  format: 'ask',
  prompt: null,
  isActive: true,
  createdAt: '2026-05-20T10:00:00.000Z',
  // work-035: Main Mini App link + короткий код (5 base62-симв.), БЕЗ /<short_name>.
  deepLink: 'https://t.me/veylme_bot?startapp=bdmock1',
  storyShareUrl: 'https://t.me/veylme_bot?startapp=bsmock1',
  inviteCardUrl: 'https://mock.local/invite-card.png',
};

/** Короткий код ссылки, резолвящийся в FIXTURE_BOX_PUBLIC (work-035, GET /boxes/link/:code). */
const MOCK_LINK_CODE = 'mock1';

const FIXTURE_BOX_PUBLIC: BoxPublicView = {
  token: 'mocktoken1234',
  format: 'ask',
  prompt: null,
  isActive: true,
};

// work-036 (Трек D): message-ящик («Напиши мне анонимно») — отдельная фикстура
// с format:'message', НЕ смешивается с ask-ящиком/инбоксом выше.
const FIXTURE_MESSAGE_BOX: BoxOwnerView = {
  id: 'box_mock_2',
  token: 'mocktoken5678',
  format: 'message',
  prompt: null,
  isActive: true,
  createdAt: '2026-06-01T10:00:00.000Z',
  deepLink: 'https://t.me/veylme_bot?startapp=bdmock2',
  storyShareUrl: 'https://t.me/veylme_bot?startapp=bsmock2',
  inviteCardUrl: 'https://mock.local/invite-card-message.png',
};

function answer(id: string, text: string, sharedToStory: boolean): AnswerView {
  return {
    id,
    text,
    cardImageUrl: null,
    sharedToStory,
    createdAt: '2026-05-28T15:41:00.000Z',
  };
}

const FIXTURE_INBOX: QuestionInboxItem[] = [
  {
    id: 'q1',
    text: 'Чо там как?',
    status: 'answered',
    moderationFlag: null,
    createdAt: '2026-05-28T15:40:10.000Z',
    answer: answer('a1', 'Норм', false),
  },
  {
    id: 'q2',
    text: 'Как дела?',
    status: 'answered',
    moderationFlag: null,
    createdAt: '2026-05-28T15:23:00.000Z',
    answer: answer('a2', 'Норм', true),
  },
  {
    id: 'q3',
    text: 'Какой у тебя любимый фильм и почему именно он зацепил тебя так сильно?',
    status: 'pending',
    moderationFlag: null,
    createdAt: '2026-05-29T10:00:00.000Z',
    answer: null,
  },
  {
    id: 'q4',
    text: 'Срочно переходи по ссылке, тут раздают крипту бесплатно!!!',
    status: 'pending',
    moderationFlag: 'flagged',
    createdAt: '2026-05-29T09:00:00.000Z',
    answer: null,
  },
  {
    id: 'q5',
    text: 'Этот вопрос был скрыт автором.',
    status: 'hidden',
    moderationFlag: null,
    createdAt: '2026-05-27T12:00:00.000Z',
    answer: null,
  },
  {
    id: 'q6',
    text: 'Заблокированный спам-вопрос.',
    status: 'removed',
    moderationFlag: 'blocked',
    createdAt: '2026-05-27T08:00:00.000Z',
    answer: null,
  },
];

// work-036 (Трек D): фикстура message-инбокса — сообщения, ответ опционален
// (та же механика Question, что и ask, но семантика «просто сообщение»).
const FIXTURE_MESSAGE_INBOX: QuestionInboxItem[] = [
  {
    id: 'm1',
    text: 'Просто хотел сказать, что ты крутой человек!',
    status: 'pending',
    moderationFlag: null,
    createdAt: '2026-06-02T09:00:00.000Z',
    answer: null,
  },
  {
    id: 'm2',
    text: 'Спасибо тебе за поддержку на прошлой неделе — это было очень важно.',
    status: 'answered',
    moderationFlag: null,
    createdAt: '2026-06-01T20:00:00.000Z',
    answer: answer('am2', 'И тебе спасибо! ❤️', false),
  },
];

const FIXTURE_NEW_ANSWER: AnswerView = answer('a_new', '(ответ сохранён)', false);

const FIXTURE_LEADERBOARD: LeaderboardResponse = {
  entries: [
    { rank: 1, boxToken: 'lb_token_1', ownerUsername: 'anna_k', format: 'ask', score: 142, questionCount: 98, referralCount: 44 },
    { rank: 2, boxToken: 'lb_token_2', ownerUsername: 'maxim', format: 'ask', score: 119, questionCount: 80, referralCount: 39 },
    { rank: 3, boxToken: 'lb_token_3', ownerUsername: null, format: 'ask', score: 87, questionCount: 71, referralCount: 16 },
    { rank: 4, boxToken: 'lb_token_4', ownerUsername: 'dasha', format: 'ask', score: 64, questionCount: 50, referralCount: 14 },
    { rank: 5, boxToken: 'lb_token_5', ownerUsername: 'igor_p', format: 'ask', score: 41, questionCount: 33, referralCount: 8 },
  ],
  nextCursor: null,
};

const FIXTURE_REWARDS: RewardsResponse = {
  level: 2,
  badges: ['viral_starter', 'storyteller'],
  themes: ['confession'],
  confirmedReferrals: 7,
  nextLevelAt: 10,
};

const FIXTURE_PREPARE_SHARE: PrepareShareResponse = {
  preparedMessageId: 'mock-prepared-message-id',
  expiresAt: '2026-06-30T00:00:00.000Z',
};

// CODE ENTRY fixtures (work-020 + work-021: +inviteCardUrl)
const FIXTURE_BOX_CODE: BoxCodeView = {
  code: '1234',
  inviteCardUrl: 'https://mock.local/invite-card-1234.png',
};
const FIXTURE_BOX_CODE_ROTATED: BoxCodeView = {
  code: '5678',
  inviteCardUrl: 'https://mock.local/invite-card-5678.png',
};
const FIXTURE_BOX_BY_CODE: BoxByCodeView = {
  format: 'ask',
  prompt: null,
  isActive: true,
};
const FIXTURE_QUESTION_BY_CODE: CreateQuestionResponse = {
  ok: true,
  questionId: 'mock-question-id-000000',
};

const OK = { ok: true } as const;

/**
 * Резолвит мок-ответ по (метод, путь). undefined → ручка не замокана.
 *
 * work-036: `POST /boxes` и `GET /inbox` — format-aware, обрабатываются ДО
 * вызова этой функции (нужен доступ к body/query, которых тут нет) — см.
 * `installMockApi` ниже.
 */
function resolve(method: string, path: string): unknown {
  if (method === 'POST' && path === '/boxes/toggle') return OK;
  if (
    method === 'POST' &&
    (path === '/questions/hide' ||
      path === '/questions/report' ||
      path === '/senders/block')
  ) {
    return OK;
  }
  if (method === 'POST' && path === '/questions') {
    const created: CreateQuestionResponse = {
      ok: true,
      questionId: 'mock-question-id-000001',
    };
    return created;
  }
  if (method === 'POST' && path === '/answers') return FIXTURE_NEW_ANSWER;
  if (method === 'POST' && path === '/share/prepare') return FIXTURE_PREPARE_SHARE;
  if (method === 'POST' && path === '/events') return OK;
  if (method === 'GET' && path === '/leaderboard') return FIXTURE_LEADERBOARD;
  if (method === 'GET' && path === '/me/rewards') return FIXTURE_REWARDS;
  // CODE ENTRY (work-020)
  if (method === 'POST' && path === '/boxes/code') return FIXTURE_BOX_CODE;
  if (method === 'POST' && path === '/boxes/code/rotate') return FIXTURE_BOX_CODE_ROTATED;
  if (method === 'POST' && path === '/questions/by-code') return FIXTURE_QUESTION_BY_CODE;
  // work-035: рекламная first-touch атрибуция — мок всегда { ok:true } (anti-oracle).
  if (method === 'POST' && path === '/users/ad-source') return OK;
  // GET /me/locale — язык не задан на сервере (клиент возьмёт detectLocale и
  // не перезатрёт локальный выбор при mount'е). PATCH обрабатывается отдельно
  // ниже (нужно тело запроса — эхо выбранного языка).
  if (method === 'GET' && path === '/me/locale') return { locale: null };
  // GET /boxes/:token (публичный view для AskScreen) — но не /boxes/toggle.
  if (method === 'GET' && /^\/boxes\/[^/]+$/.test(path)) return FIXTURE_BOX_PUBLIC;
  return undefined;
}

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Подменяет `window.fetch`: запросы к нашему API (base из VITE_API_BASE_URL)
 * обслуживаются фикстурами, всё прочее уходит в оригинальный fetch.
 * Имитирует ~120ms сетевой задержки, чтобы увидеть loading-состояния.
 */
export function installMockApi(): void {
  if (!env.VITE_USE_TG_MOCK) return;
  const realFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : input.url;
    const method = (init?.method ?? 'GET').toUpperCase();

    let url: URL;
    try {
      url = new URL(rawUrl, window.location.origin);
    } catch {
      return realFetch(input, init);
    }
    const path = url.pathname;

    // GET /boxes/by-code/:code — 2 сегмента; отдельный handler ДО resolve
    // (нужен 404 для неизвестных кодов, resolve всегда возвращает 200).
    // Коллизий с ^/boxes/[^/]+$ нет: тот regex — GET-only 1 сегмент
    // (path=/boxes/:token), by-code = path=/boxes/by-code/:code (2 сегмента).
    const byCodeMatch =
      method === 'GET' && /^\/boxes\/by-code\/(.+)$/.exec(path);
    if (byCodeMatch) {
      const code = byCodeMatch[1];
      await new Promise((r) => setTimeout(r, 120));
      if (code === '1234') {
        // eslint-disable-next-line no-console
        console.info(`[mockApi] ${method} ${path} → mock 200`);
        return jsonResponse(FIXTURE_BOX_BY_CODE);
      }
      // eslint-disable-next-line no-console
      console.info(`[mockApi] ${method} ${path} → mock 404`);
      return jsonResponse({ error: 'not_found' }, 404);
    }

    // GET /boxes/link/:code (work-035) — резолв короткого кода ссылки в
    // ПОЛНЫЙ BoxPublicView (с token). Отдельный handler ДО resolve (нужен 404
    // для неизвестных кодов). Коллизий с ^/boxes/[^/]+$ нет: тот regex — GET-only
    // 1 сегмент (path=/boxes/:token), link = path=/boxes/link/:code (2 сегмента).
    const linkCodeMatch = method === 'GET' && /^\/boxes\/link\/(.+)$/.exec(path);
    if (linkCodeMatch) {
      const code = linkCodeMatch[1];
      await new Promise((r) => setTimeout(r, 120));
      if (code === MOCK_LINK_CODE) {
        // eslint-disable-next-line no-console
        console.info(`[mockApi] ${method} ${path} → mock 200`);
        return jsonResponse(FIXTURE_BOX_PUBLIC);
      }
      // eslint-disable-next-line no-console
      console.info(`[mockApi] ${method} ${path} → mock 404`);
      return jsonResponse({ error: 'not_found' }, 404);
    }

    // GET /questions/:id/answer — retention-answer-loop (MyQuestionsScreen).
    // Мок: id заканчивается на '000001' → уже отвечен (демонстрирует
    // answered-состояние), любой другой id → «ждём ответа».
    const answerMatch =
      method === 'GET' && /^\/questions\/([^/]+)\/answer$/.exec(path);
    if (answerMatch) {
      const id = answerMatch[1] ?? '';
      await new Promise((r) => setTimeout(r, 120));
      const view: QuestionAnswerView = id.endsWith('000001')
        ? {
            answered: true,
            answer: {
              text: '(мок) Норм, спасибо что спросил!',
              cardImageUrl: null,
              createdAt: '2026-05-28T15:41:00.000Z',
            },
          }
        : { answered: false, answer: null };
      // eslint-disable-next-line no-console
      console.info(`[mockApi] ${method} ${path} → mock (answered=${view.answered})`);
      return jsonResponse(view);
    }

    // PATCH /me/locale — эхо выбранного языка из тела (resolve не видит body).
    // Форма ответа = MeLocaleResponse `{ locale }`, иначе клиент откатит выбор.
    if (method === 'PATCH' && path === '/me/locale') {
      await new Promise((r) => setTimeout(r, 120));
      let locale: unknown = null;
      try {
        locale = JSON.parse(typeof init?.body === 'string' ? init.body : '{}').locale;
      } catch {
        /* тело не JSON — вернём null */
      }
      // eslint-disable-next-line no-console
      console.info(`[mockApi] ${method} ${path} → mock (locale=${String(locale)})`);
      return jsonResponse({ locale });
    }

    // POST /boxes — format-aware (work-036 Трек D): читаем `format` из тела,
    // чтобы вернуть ask-фикстуру (дефолт) либо FIXTURE_MESSAGE_BOX.
    if (method === 'POST' && path === '/boxes') {
      await new Promise((r) => setTimeout(r, 120));
      let format: unknown = 'ask';
      try {
        format =
          JSON.parse(typeof init?.body === 'string' ? init.body : '{}').format ?? 'ask';
      } catch {
        /* тело не JSON — дефолт 'ask' */
      }
      const box = format === 'message' ? FIXTURE_MESSAGE_BOX : FIXTURE_BOX;
      // eslint-disable-next-line no-console
      console.info(`[mockApi] ${method} ${path} → mock (format=${String(format)})`);
      return jsonResponse(box);
    }

    // GET /inbox — format-aware: `?format=message` → сообщения message-ящика,
    // без параметра → обычный ask-инбокс (как раньше, CANON-совместимо).
    if (method === 'GET' && path === '/inbox') {
      await new Promise((r) => setTimeout(r, 120));
      const format = url.searchParams.get('format');
      const inboxBody =
        format === 'message'
          ? { box: FIXTURE_MESSAGE_BOX, items: FIXTURE_MESSAGE_INBOX, nextCursor: null }
          : { box: FIXTURE_BOX, items: FIXTURE_INBOX, nextCursor: null };
      // eslint-disable-next-line no-console
      console.info(`[mockApi] ${method} ${path} → mock (format=${String(format)})`);
      return jsonResponse(inboxBody);
    }

    const body = resolve(method, path);
    if (body === undefined) {
      // eslint-disable-next-line no-console
      console.warn(`[mockApi] нет мока для ${method} ${path} — иду в реальный fetch`);
      return realFetch(input, init);
    }

    await new Promise((r) => setTimeout(r, 120));
    // eslint-disable-next-line no-console
    console.info(`[mockApi] ${method} ${path} → mock`);
    return jsonResponse(body);
  };
}
